import React, {useContext, useEffect, useState, useRef} from 'react';
import {AccountsContext} from "../../context/AccountsProvider";

export default function AccountsModal({isOpen, onClose, account, vendorId, onSuccess, onError}) {
  const {sendInvite, createAccount, updateAccount, getAccounts} = useContext(AccountsContext);
  const [formData, setFormData] = useState({username: '', email: '', role: 3, password: ''});
  const [manual, setManual] = useState(false);
  const modalRef = useRef();

  useEffect(() => {
    const handleMouseUp = (event) => {
      if (isOpen && modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("keydown", handleKeyDown);

    if (isOpen && account) {
      setFormData({username: account.username, email: account.email, role: account.role, password: ''});
    } else if (isOpen && !account) {
      setFormData({username: '', email: '', role: 3, password: ''});
    }

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, account, onClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (manual) {
        const dataToSend = {...formData, vendor: vendorId, role: 3};

        if (account && account.id) {
          await updateAccount(account.id, dataToSend, vendorId);
          onSuccess("Account updated successfully.");
        } else {
          await createAccount(dataToSend);
          onSuccess("Account created successfully.");
        }
      } else {
        await sendInvite(formData.email, vendorId);
        onSuccess("Invitation sent successfully.");
      }
      onClose();
      getAccounts(vendorId);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        onError('User account already exists. Please try again with a different email.');
      } else {
        console.error("Error occurred during submission:", error);
        onError('An error occurred while saving the account. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const toggleManual = () => {
    setManual(!manual);
    setFormData({username: '', email: '', role: 3, password: ''});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="fixed inset-0 bg-stone-950 bg-opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <div
          ref={modalRef}
          className="inline-block align-bottom bg-white dark:bg-stone-700 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div className="flex flex-col h-full">
            <div
              className="bg-white dark:bg-stone-900 pt-5 sm:pb-4 px-8 w-full flex-grow flex items-center justify-center">
              <div className="sm:flex sm:items-center">
                <div className="text-center sm:text-left">
                  <div className="flex justify-between items-center py-2">
                    <h2 className="text-lg font-bold dark:text-gray-200">
                      {manual ? "Add New Account" : "Send Invitation"}
                    </h2>
                    <button onClick={onClose}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-gray-600 hover:text-gray-800 dark:text-gray-500 dark:hover:text-gray-700"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                  <form className="mt-2" onSubmit={handleSubmit}>
                    {manual ? (
                      <>
                        <div className="mb-4">
                          <label htmlFor="username"
                                 className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                          <input type="text" id="username" name="username" value={formData.username}
                                 onChange={handleChange}
                                 className="mt-1 block w-full px-3 py-2 bg-white dark:bg-stone-800 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                 placeholder="Username"
                                 autoComplete="off"/>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="password"
                                 className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                          <input type="text" id="password" name="password" value={formData.password}
                                 onChange={handleChange}
                                 className="mt-1 block w-full px-3 py-2 bg-white dark:bg-stone-800 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                 placeholder="Password"
                                 autoComplete="off"/>
                        </div>
                      </>
                    ) : null}
                    <div className="mb-4">
                      <label htmlFor="email"
                             className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                             className="mt-1 block w-full px-3 py-2 bg-white dark:bg-stone-800 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                             placeholder="Email Address"
                             autoComplete="off"/>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <button
                        type="button"
                        onClick={toggleManual}
                        className="font-semibold text-sm text-blue-600 dark:text-indigo-600 cursor-pointer bg-transparent border-none p-0"
                      >
                        {manual ? "Send invitation instead" : "Manually create account"}
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <button type="button"
                              className="bg-gray-500 w-32 text-white font-bold uppercase text-sm px-6 py-3 rounded shadow hover:bg-gray-600 focus:outline-none focus:shadow-outline"
                              onClick={onClose}>
                        Cancel
                      </button>
                      <button type="submit"
                              className="bg-blue-500 w-32 dark:bg-indigo-500 text-white font-bold uppercase ml-3 text-sm px-6 py-3 rounded shadow hover:bg-blue-600 focus:outline-none focus:shadow-outline">
                        {manual ? "Save" : "Send"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
